import {
  createModelProvider,
  externalModelUpdate,
  loadNewForm,
  triggerEvent,
  externalRunBinds,
  updateConfig
} from "@icg360/rex";
import { produce } from "immer";
import { batch } from "react-redux";
import { pick, cloneDeep, remove } from "lodash";
import { add, isAfter, isBefore } from "date-fns";

import { track } from "../units/tracking";
import { QTC, LOSS_HISTORY, QUOTE_CONST, MORTGAGEE_CHANGE } from "../constants";
import {
  commercialFusions,
  plFusions
} from "../components/quote-transaction-component/fusions";
import { flattenInputsIgnoreFusions } from "../components/quote-transaction-component/utils";
import shapeDataForLaunchSignDocumentModal from "../components/quote-transaction-component/utils/shape-data-for-launch-sign-document-modal";
import AP from "../legacy/global";
import {
  getForms,
  getProduct,
  getQuoteCached,
  patchQuote,
  putQuote
} from "../promises/quotes";
import { queryClient } from "../units/react-query-client";
import {
  parseDate,
  printDate,
  validateEffectiveDate
} from "../utils/dateParser";
import { getApiUrl } from "../utils/get-api-url";
import { GetMortgageeProperties } from "../utils/get-mortgagee-properties";
import {
  TRIUM_CARRIER_IDS,
  VAVE_CARRIER_NAME
} from "../components/quote-constants";
import { QUOTE_UTILITY_TABS } from "../components/quote-transaction-component/quote-utility/constants";
import { UPDATE_QUOTE_UTILITY_ACTIVE_TAB } from "../components/quote-transaction-component/qtc-constants";
import { isCarrierAvailable } from "../utils/is-carrier-available";

import { hasFeature, pickEnvInfo, pickQTC } from "./selectors";
import {
  memoDeriveForm,
  selectQTCModals,
  selectQTCQuote,
  selectIsCommercial,
  selectQuoteInputs
} from "./selectors/qtc-selectors";

const INPUT_SOURCES = MORTGAGEE_CHANGE.INPUT_SOURCES;

export const externalQTCReset = {
  type: QTC.EXTERNAL_RESET
};

const startLoading = (quoteNumber, step) => ({
  type: QTC.START_LOADING,
  quoteNumber,
  step
});

export const quoteLoadSuccess = ({ quote }) => ({
  type: QTC.LOAD_QUOTE_SUCCESS,
  quote
});

export const loadSuccess = ({ quote, product }) => ({
  type: QTC.LOAD_SUCCESS,
  quote,
  product
});

const errorLoading = message => ({
  type: QTC.LOAD_ERROR,
  message
});

export const saveSuccess = (data, { lossHistory, bind } = {}) => ({
  type: QTC.SAVE_SUCCESS,
  data,
  lossHistory,
  bind
});

export const toggleQTCModal =
  ({ modal }) =>
  (dispatch, getState) => {
    const isOpen = !selectQTCModals(getState())[modal];
    dispatch({
      type: QTC.SET_MODAL_STATE,
      modal,
      isOpen
    });
  };

export const pushChangeHistory = ({ title, list }) => ({
  type: QTC.PUSH_CHANGE_HISTORY,
  change: { title, list }
});

export const clearChangeHistoryAction = {
  type: QTC.CLEAR_CHANGE_HISTORY
};

export const updateQuoteUtilityActiveTab = tab => ({
  type: UPDATE_QUOTE_UTILITY_ACTIVE_TAB,
  tab
});

export const loadForms =
  (quoteNumber, currentStep) => async (dispatch, getState) => {
    dispatch(startLoading(quoteNumber, currentStep));
    try {
      // 1. get quote data, product details and forms

      const [product, quote] = await Promise.all([
        getProduct({
          id: quoteNumber
        }),
        getQuoteCached({ id: quoteNumber })
      ]);

      dispatch(quoteLoadSuccess({ quote }));
      const forms = await getForms({ productId: product.id });

      if (forms.error) {
        console.error(forms.error);
        throw new Error(forms.message);
      }

      const { alc, username, fullname, licenseNumber } = pickEnvInfo(
        getState()
      );

      batch(() => {
        // 2. add config for dispatchers
        dispatch(
          updateConfig({
            services: {
              ixprofiler: {
                universal: `${getApiUrl("ixprofiler")}/templates`
              }
            }
          })
        );

        // remove launchMap button
        const filteredForms = cloneDeep(forms);
        const quoteInputs = filteredForms[0]?.groups[0]?.inputs;

        if (quoteInputs) {
          remove(filteredForms[0]?.groups[0]?.inputs, {
            id: QTC.QTC_TRACK.QUOTE_FORM.LAUNCH_MAP_ID
          });
        }

        // 3. load form into rex
        dispatch(
          loadNewForm({
            forms: filteredForms,
            id: QTC.QTC_FORM_ID,
            initialData: {
              LicenseNumber: licenseNumber,
              Name: fullname,
              ...quote.inputs,
              ...quote.flags
            },
            elementInjections: {
              PolicyRequest: {
                locationid: alc,
                username,
                useragent: "QTC"
              },
              EffectiveDateRange: quote.effectiveDate,
              CreatedDate: quote.createDatetime
            }
          })
        );

        // 4. save quote details
        dispatch(
          loadSuccess({
            quote,
            product
          })
        );
      });
    } catch (err) {
      console.error(err);
      return dispatch(errorLoading(err.message));
    }
  };

const mapRetrievedLosses = retrieved =>
  retrieved.reduce(
    (acc, loss) => ({
      ...acc,
      [`LossAmount${loss.id}Retrieved`]: loss.amount,
      [`LossType${loss.id}Retrieved`]: loss.type,
      [`LossDate${loss.id}Retrieved`]: loss.date,
      [`LossCatIndicator${loss.id}Retrieved`]: loss.catIndicator,
      [`LossDescription${loss.id}Retrieved`]: loss.description,
      [`LossPolicyNumber${loss.id}`]: loss.policyNumber,
      [`LossLocation${loss.id}`]: loss.location,
      [`LossCaseNumber${loss.id}`]: loss.caseNumber,
      [`LossCarrier${loss.id}`]: loss.carrier,
      [`LossClaimStatus${loss.id}`]: loss.claimStatus,
      [`LossMatchType${loss.id}`]: loss.matchType,
      [`LossAPlusCode${loss.id}`]: loss.aPlusCode
    }),
    {}
  );

const addressValidationInputs = [
  "DesertIndicator",
  "WindSpeedMaxAnnualMean",
  "HumidityAprilMeanPct",
  "FDID",
  "FDIDVersion",
  "FireStationNameFSC",
  "FireStationCredibility"
];
const roofResealingInputs = ["RoofResealingStatus"];

// Update the body with non-product inputs if they exist
const updateNonProductInputs = (quote, body) => {
  const keys = [...addressValidationInputs, ...roofResealingInputs];
  keys.forEach(key => {
    if (quote.inputs[key]) {
      body.inputs[key] = quote.inputs[key];
    }
  });
};

let lastSave = 0;
export const saveQuote =
  ({
    newStepToSave = null,
    runFormValidation = false,
    hasStepChanged = false,
    runLossHistory = false,
    runAlternates = false,
    runAAL = false
  } = {}) =>
  async (dispatch, getState) => {
    const thisSave = Date.now();
    lastSave = thisSave;
    const state = getState();
    const {
      loading,
      effectiveDate,
      quote,
      saveQueue,
      saving,
      quoteUtilityActiveTab
    } = pickQTC(state);
    const hasQuoteSaveQueue = hasFeature(state, "quote-save-queue");
    const hasExpandedMarketsRecommend = hasFeature(
      state,
      "expanded-markets-recommend"
    );
    const isCommercial = selectIsCommercial(state);

    // Don't save if there is no quote. Quote is set to null when switching
    // TODO figure out what to do with this - on quote home, a save happens even before the quote is loaded into redux
    // so this will happen everytime on first load to quote home
    if (!quote) {
      // eslint-disable-next-line no-console
      console.warn(
        "Tried to save the quote but the quote is no longer loaded."
      );
      return;
    }

    const currentCarrier = quote.carriers[0];
    const isCurrentCarrierVave = currentCarrier.nameShort === VAVE_CARRIER_NAME;
    const isCurrentCarrierAvailable = isCarrierAvailable(currentCarrier);
    const runAlternatesOnQuoteUtilityMessageTab =
      quoteUtilityActiveTab === QUOTE_UTILITY_TABS.MESSAGES &&
      hasExpandedMarketsRecommend &&
      !isCurrentCarrierVave &&
      !isCurrentCarrierAvailable;
    const runAlertnatesOnQuoteUtilityCompareTab =
      quoteUtilityActiveTab === QUOTE_UTILITY_TABS.COMPARE_PRODUCTS;

    // In this case, we tried to save the quote without changing the step
    // but the step that was loaded is not in the quote yet.
    // This can happen if you change an input but the debounce for the save happens after you click next
    if (!newStepToSave && loading.step !== quote.inputs.CurrentStep) {
      // eslint-disable-next-line no-console
      console.warn(
        "Prevented a quote save because changing the step should be responsible for saving the quote and changing the input."
      );
    }

    const getModel = createModelProvider(state, {
      type: "JSON",
      onlyTerms: true,
      id: QTC.QTC_FORM_ID,
      index: 0
    });
    const { changed, values } = getModel("default");

    // No saveQuote because there were no changes made.
    if (hasStepChanged && !changed.length && !runAAL) {
      return;
    }

    // Do not save if property location address is empty
    if (values.InsuredMailingAddressLine1?.trim() === "") {
      return;
    }

    const body = {
      ...pick(quote, [
        "carrier",
        "createUserId",
        "effectiveDate",
        "productType",
        "flags"
      ]),
      inputs: values
    };

    // Trium POC: if runAAL is true, add to body.inputs
    if (TRIUM_CARRIER_IDS.includes(body.carrier) && runAAL) {
      body.inputs.ReplacementCostModalClosed = true;
      body.inputs.AnalysisValidated = "200";
      body.inputs.AnalysisInProcess = "200";
    }

    // Set the CurrentStep input, this is necessary on the server but not in Rex
    body.inputs.CurrentStep = newStepToSave || quote.inputs.CurrentStep;

    // Add in non-product inputs if they exist
    updateNonProductInputs(quote, body);

    // If effective date is invalid only save if there are changes and use the
    // existing effective date.
    if (!effectiveDate.error && effectiveDate.value) {
      // If effective date and/or expiration is set, update in the request body
      body.effectiveDate = effectiveDate.value;
      body.inputs.EffectiveDateRange = effectiveDate.value;
      body.inputs.ExpirationDate = printDate(
        add(parseDate(effectiveDate.value), { years: 1 }),
        "yyyy-MM-dd"
      );
    }

    // Any call of saveQuote will need to explicitly opt in to validating the current
    // form. For example, Quote Home will look like quote form is active but we don't
    // care to validate when we make a save on that page.
    if (
      runFormValidation &&
      typeof memoDeriveForm[loading.step] === "function"
    ) {
      const fusions = isCommercial ? commercialFusions : plFusions;
      const derivedForm = memoDeriveForm[loading.step](state, { fusions });
      const hasErrors = flattenInputsIgnoreFusions(derivedForm).some(
        ({ validationErrors = [] }) => validationErrors.length > 0
      );
      if (hasErrors) {
        // eslint-disable-next-line no-console
        console.warn(
          "Cancelled the save because some form fields were invalid"
        );
        return;
      }
    }

    if (hasQuoteSaveQueue) {
      if (saveQueue.status === QTC.SAVE_QUEUE.STATUS.PENDING) {
        dispatch({
          type: QTC.SAVE_QUEUE.ATTEMPTED,
          props: {
            newStepToSave,
            runFormValidation,
            hasStepChanged,
            runLossHistory,
            runAAL,
            runAlternates
          }
        });

        return;
      } else {
        dispatch({ type: QTC.SAVE_QUEUE.STATUS.PENDING });
      }
    }

    dispatch({
      type: QTC.SEND_SAVE,
      step: newStepToSave,
      lossHistory: runLossHistory
    });
    try {
      const data = await putQuote({
        id: quote.quoteId,
        data: body,
        headers: {
          Eligibility: !!runLossHistory,
          LossAnalysis: !!runLossHistory,
          runAlternates:
            runAlternates ||
            isCommercial ||
            runAlertnatesOnQuoteUtilityCompareTab ||
            runAlternatesOnQuoteUtilityMessageTab
        }
      });
      batch(() => {
        const passToModel = {
          ...data.flags,
          ...pick(data.inputs, QTC.PERSISTED_TERMS)
        };
        dispatch(saveSuccess(data, { lossHistory: runLossHistory }));
        if (runLossHistory) {
          // If the flag is on, the data will look like:
          // losses.losses = [[{loss1-loss},{loss1-loss}], [{loss2}], [{loss3}], ...]
          // the old way was to have arrays of losses inside keys for "retrieved"
          // the new way has a key "lossType" in the loss object that will tell
          // us if it's retrieved or admitted/provided
          const retrieved = data.losses
            .flat()
            .filter(loss => loss.lossType === "retrieved");
          const retrievedLosses = mapRetrievedLosses(retrieved);
          dispatch(
            externalModelUpdate({
              data: {
                ...passToModel,
                ...retrievedLosses
              },
              formId: QTC.QTC_FORM_ID
            })
          );
        }
        // If some flags or terms (listed in PERSISTED_TERMS) came back from the
        // server different from what we have let's update Rex. For example the
        // "ReviewLock" when losses are triggered
        else if (
          Object.entries(passToModel).some(
            ([key, value]) =>
              quote.flags[key] !== value && quote.inputs[key] !== value
          )
        ) {
          dispatch(
            externalModelUpdate({
              data: passToModel,
              formId: QTC.QTC_FORM_ID
            })
          );
        }

        if (hasQuoteSaveQueue) {
          dispatch({ type: QTC.SAVE_QUEUE.STATUS.SUCCESS });
          const { saveAttempted: queueSaveAttempted, props: queueProps } =
            pickQTC(getState()).saveQueue.queue;

          if (queueSaveAttempted) {
            dispatch({ type: QTC.SAVE_QUEUE.RESET });
            dispatch(saveQuote(queueProps));
          } else {
            dispatch({ type: QTC.SAVE_QUEUE.STATUS.IDLE });
          }
        }
      });
    } catch (err) {
      if (thisSave === lastSave) {
        dispatch({
          type: QTC.SAVE_ERROR,
          message: err.message || QUOTE_CONST.SAVESTATES.FAILED,
          lossHistory: runLossHistory
        });

        if (hasQuoteSaveQueue) {
          dispatch({ type: QTC.SAVE_QUEUE.RESET });
          dispatch({ type: QTC.SAVE_QUEUE.STATUS.REJECTED });
        }
      }
    }
  };

export const setRCEModalData = payload => ({
  type: QTC.SET_RCE_MODAL_DATA,
  payload
});

export const updateQuote = updatedQuote => async dispatch => {
  dispatch(
    externalModelUpdate({
      data: updatedQuote,
      formId: QTC.QTC_FORM_ID
    })
  );
  dispatch(saveQuote({ runFormValidation: true }));
};

export const processPayment = () => async (dispatch, getState) => {
  const thisSave = Date.now();
  lastSave = thisSave;
  const state = getState();
  const isCommercial = selectIsCommercial(state);
  const { quote, ccFormData, achFormData, checkFormData } = pickQTC(state);
  const { PaymentPlanType } = selectQuoteInputs(state);

  const getModel = createModelProvider(state, {
    type: "JSON",
    onlyTerms: true,
    id: QTC.QTC_FORM_ID,
    index: 0
  });
  const { values } = getModel("default");

  const { PaymentMethod } = values;

  let request = null;

  // Credit Card
  if (PaymentMethod === "400") {
    request = pick(
      ccFormData,
      "payorFirstName",
      "payorLastName",
      "billingAddressLine1",
      "billingAddressZipCode",
      "method",
      "accountNumber",
      "creditCardType",
      "creditCardExpirationMonth",
      "creditCardExpirationYear"
    );
  }

  // ACH
  if (PaymentMethod === "300") {
    request = pick(
      achFormData,
      "accountType",
      "bankName",
      "billingAddressLine1",
      "billingAddressCity",
      "billingAddressState",
      "billingAddressZipCode",
      "method",
      "payorFirstName",
      "payorLastName",
      "accountNumber",
      "routingNumber"
    );

    request.easyPayEnrollmentAcknowledgement =
      values.EasyPayEnrollmentRequested;
    request.easyPayDraftDay = values.EasyPayDraftDay;
    request.insuredEmailAddressBilling = values.InsuredEmailAddressBilling;
  }

  // Check
  if (PaymentMethod === "200" && PaymentPlanType !== "invoice") {
    request = pick(checkFormData, "accountHolder", "checkNumber");
  }

  dispatch({ type: QTC.SEND_SAVE, bind: true });

  const body = {
    inputs: values
  };

  if (request) {
    body.payment = { request };
  }

  try {
    const data = await patchQuote({
      id: quote.quoteId,
      data: body,
      headers: {
        Submission: true
      }
    });
    // Error handling
    const { stages } = data.carriers[0];

    if (
      stages.submission.errors &&
      stages.submission.errors.length > 0 &&
      stages.submission.errors[0].message
    ) {
      dispatch({
        type: QTC.PAYMENT_ERROR,
        payload: {
          error: "payment_error",
          title: "Your payment has failed",
          message: stages.submission.errors[0].message,
          errorCode: stages.submission.errors[0].type
        }
      });
    } else if (!stages.submission.complete) {
      dispatch({
        type: QTC.PAYMENT_ERROR,
        payload: {
          error: "payment_error",
          title: "Something went wrong",
          message: "We weren't able to bind your quote. Please try again.",
          errorCode: "An unknown error occurred."
        }
      });
    } else {
      dispatch(saveSuccess(data, { bind: true }));
      AP.launchSignDocumentModal(
        shapeDataForLaunchSignDocumentModal(data, isCommercial)
      );
    }
  } catch (err) {
    console.error(err);
    if (thisSave === lastSave) {
      dispatch({
        type: QTC.SAVE_ERROR,
        message: err.message || QUOTE_CONST.SAVESTATES.FAILED
      });
    }
  }
};

export const updateCcFormData = input => ({
  type: QTC.UPDATE_CC_FORM,
  input
});
export const updateAchFormData = input => ({
  type: QTC.UPDATE_ACH_FORM,
  input
});
export const updateCheckFormData = input => ({
  type: QTC.UPDATE_CHECK_FORM,
  input
});

// Search Mortgagee Actions
export const toggleMortgageeModal = (open, initialData = {}) => ({
  type: QTC.TOGGLE_SEARCH_MORTGAGEE_MODAL,
  payload: { open, initialData }
});

export const userAddMortgagee = () => (dispatch, getState) => {
  const state = getState();
  const activeIndex = pickQTC(state).searchMortgagee.initialData.Position;
  const getModel = createModelProvider(state, {
    type: "JSON",
    id: QTC.QTC_FORM_ID,
    childId: "newMortgagee"
  });
  const { values } = getModel("mortgagee");
  const mortgageeInputs = {
    [`MortgageeNumber${activeIndex}`]: values["company"],
    [`Mortgagee${activeIndex}AddressCity`]: values["city"],
    [`Mortgagee${activeIndex}AddressLine1`]: values["address-line-one"],
    [`Mortgagee${activeIndex}AddressLine2`]: values["address-line-two"],
    [`Mortgagee${activeIndex}AddressState`]: values["state"],
    [`Mortgagee${activeIndex}AddressZip`]: values["zip"],
    [`Mortgagee${activeIndex}Clause1`]: values["clause1"],
    [`LoanNumber${activeIndex}`]: values["loan-number"],
    [`Mortgagee${activeIndex}AddedByUser`]: "100"
  };
  track("Mortgagee Updated", {
    mortgageeInputs,
    inputSource: INPUT_SOURCES.MANUAL_INPUT
  });
  batch(() => {
    dispatch(
      externalModelUpdate({
        data: mortgageeInputs,
        formId: QTC.QTC_FORM_ID
      })
    );
    dispatch(triggerEvent({ eventName: "mortgagee-update" }));
    dispatch(externalRunBinds());
    dispatch(saveQuote({ runFormValidation: true }));
    dispatch(toggleMortgageeModal(false));
  });
};

export const saveMortgagee = mortgagee => (dispatch, getState) => {
  const { AddressLine1, AddressLine2, City, Name, State, ZipCode } =
    GetMortgageeProperties(
      getState().featureFlags["updated-mortgagees-response"],
      mortgagee
    );

  const activeIndex = pickQTC(getState()).searchMortgagee.initialData.Position;
  const mortgageeInputs = {
    [`MortgageeNumber${activeIndex}`]: Name,
    [`Mortgagee${activeIndex}AddressCity`]: City,
    [`Mortgagee${activeIndex}AddressLine1`]: AddressLine1,
    [`Mortgagee${activeIndex}AddressLine2`]: AddressLine2,
    [`Mortgagee${activeIndex}AddressState`]: State,
    [`Mortgagee${activeIndex}AddressZip`]: ZipCode,
    [`Mortgagee${activeIndex}AddedByUser`]: "200"
  };

  batch(() => {
    dispatch(
      externalModelUpdate({
        data: mortgageeInputs,
        formId: QTC.QTC_FORM_ID
      })
    );
    dispatch(triggerEvent({ eventName: "mortgagee-update" }));
    dispatch(externalRunBinds());
    dispatch(saveQuote({ runFormValidation: true }));
    dispatch(toggleMortgageeModal(false));
  });
};

export const setEffectiveDate = effectiveDate => dispatch => {
  dispatch({ type: QTC.SET_EFFECTIVE_DATE, effectiveDate });
  dispatch(
    externalModelUpdate({
      elementInjections: {
        EffectiveDateRange: effectiveDate
      },
      formId: QTC.QTC_FORM_ID
    })
  );
  dispatch(triggerEvent({ eventName: "effectivedate-update" }));
  dispatch(externalRunBinds());
};

// Test whether the effective date is out of the product's range
// If it is then reload the product
// If a callback is passed, use that instead
export const isEffectiveDateOutOfRange = callback => (dispatch, getState) => {
  const { effectiveDate, loading, quote } = pickQTC(getState());
  // In this case don't use the effectiveDate from the store
  // read the effectiveDate on the quote to see if we've
  // saved an effectiveDate that is out of range from the
  // product that is loaded.
  const effectiveDateMoment = parseDate(quote.effectiveDate);
  if (
    isAfter(effectiveDateMoment, parseDate(effectiveDate.range.ceiling)) ||
    isBefore(effectiveDateMoment, parseDate(effectiveDate.range.floor))
  ) {
    if (callback) {
      callback();
    } else {
      dispatch(loadForms(loading.quoteNumber, loading.step));
    }
  }
};

export const closeEffectiveDateOutOfRangeModal =
  () => async (dispatch, getState) => {
    const { quoteId } = selectQTCQuote(getState());
    dispatch(toggleQTCModal({ modal: "effectiveDateOutOfRange" }));
    await dispatch(loadForms(quoteId, QTC.STEP_QUOTE));

    // Product might have changed so invalidate the layout call for this quote
    await queryClient.invalidateQueries(["quotes.getContext", { id: quoteId }]);

    dispatch(saveQuote());
  };

export const loadStep =
  (quoteNumber, step, isQuoteHome = false) =>
  async (dispatch, getState) => {
    const { loading } = pickQTC(getState());

    // We need to update the term CurrentStep on the quote. As far as the quote is
    // concerned, we don't really care if we are on the select step or not so just
    // pretend the select step is the quote step. This saves us from doing extra saves.
    const newStepToSave = step === QTC.STEP_SELECT ? QTC.STEP_QUOTE : step;

    // This is the first time loading this quote
    if (loading.quoteNumber !== quoteNumber) {
      await dispatch(loadForms(quoteNumber, step));
      const { quote } = pickQTC(getState());
      dispatch(
        saveQuote({
          newStepToSave,
          // If the step is the same on the quote then only do a save if
          // the initial load made other changes
          hasStepChanged: newStepToSave === quote?.inputs?.CurrentStep,
          runAlternates: isQuoteHome
        })
      );
      return;

      // The step is changing
    } else if (loading.step !== step) {
      const { quote } = pickQTC(getState());
      // Only save if the new step doesn't match what is on the quote
      if (quote && newStepToSave !== quote?.inputs?.CurrentStep) {
        dispatch(
          saveQuote({
            newStepToSave,
            runAlternates: isQuoteHome
          })
        );
      }

      // Always save on opening Quote Home to refresh the list of alternative products
      // this is needed because of how our hub & spoke model works
      // QTC is being rendered in the background
      // and because we're on the quotes step when it renders the first time, the first save will not contain the runAlternates header
      // this causes the carrier list on Quote Home to not have the list of alternative products
    } else if (isQuoteHome) {
      dispatch(saveQuote({ runAlternates: true }));
    }
  };

const initState = {
  quote: null,
  quoteUtilityActiveTab: QUOTE_UTILITY_TABS.PROPERTY_INSIGHT,
  loading: {
    state: null, // Either START_LOADING, LOAD_ERROR, or LOAD_SUCCESS
    quoteNumber: "", // What quote number was or is loading, compare with quote number in the route
    message: null, // Message if LOAD_ERROR
    step: null // Step that was or is loading to compare with the next step to load
  },
  saving: {
    sending: false,
    binding: false, // Is this save attempting to bind the quote
    lastSaved: null, // Timestamp of last successful save
    error: null // Message if an error occured.
  },
  payment: {
    title: null,
    message: null,
    error: null,
    errorType: null,
    bound: false
  },
  modals: {
    copyQuote: false,
    effectiveDateOutOfRange: false,
    replacementCost: false,
    paymentError: false,
    paymentSuccess: false
  },
  replacementCost: {
    error: null,
    replacementCostData: {}
  },
  effectiveDate: {
    value: null, // String? - YYYY-MM-DD
    error: null, // String?
    range: {
      floor: null, // String? - YYYY-MM-DDT00:00:00 (ISO 8601 without timezone)
      ceiling: null // String? - YYYY-MM-DDT00:00:00 (ISO 8601 without timezone)
    }
  },
  changeHistory: {
    showModal: false,
    changes: []
  },
  currentProductId: null,
  currentTask: {
    tasks: {}
  },
  lossHistoryReport: {
    fetch: QTC.FETCH.STATUS.INITIAL
  },
  searchMortgagee: {
    modalOpen: false,
    initialData: {}
  },
  achFormData: {
    accountType: null,
    bankName: null,
    routingNumber: null,
    accountNumber: null,
    payorFirstName: null,
    payorLastName: null,
    billingAddressLine1: null,
    billingAddressLine2: null,
    billingAddressCity: null,
    billingAddressState: null,
    billingAddressZipCode: null,
    method: "ach"
  },
  ccFormData: {
    payorFirstName: null,
    payorLastName: null,
    accountNumber: null,
    creditCardType: null,
    billingAddressLine1: null,
    billingAddressZipCode: null,
    creditCardExpirationMonth: null,
    creditCardExpirationYear: null,
    method: "creditcard"
  },
  checkFormData: {
    accountHolder: null,
    checkNumber: null,
    method: "check"
  },
  saveQueue: {
    status: QTC.SAVE_QUEUE.STATUS.IDLE, // QUEUE_IDLE, QUEUE_PENDING, QUEUE_SUCCESS, QUEUE_REJECTED
    queue: {
      saveAttempted: false,
      props: {},
      data: {}
    }
  }
};

export const quoteTransactionComponent = produce((draft, action) => {
  switch (action.type) {
    case QTC.EXTERNAL_RESET:
      // In Immer, if you don't touch the draft you can return a new state object.
      // This is to reset our state when starting a new quote.
      // https://immerjs.github.io/immer/docs/return
      return initState;
    case QTC.START_LOADING:
      return {
        ...initState,
        loading: {
          state: QTC.START_LOADING,
          quoteNumber: action.quoteNumber,
          message: null,
          step: action.step
        }
      };
    case QTC.LOAD_ERROR:
      draft.loading = {
        ...draft.loading,
        state: QTC.LOAD_ERROR,
        message: action.message
      };
      return;
    case QTC.LOAD_SUCCESS:
      draft.loading.state = QTC.LOAD_SUCCESS;
      draft.loading.message = null;
      draft.quote = action.quote;
      draft.effectiveDate.value = action.quote.effectiveDate;
      draft.effectiveDate.error = validateEffectiveDate(
        action.quote.effectiveDate,
        add(new Date(), { years: 1 })
      );
      draft.effectiveDate.range.floor = action.product.effectiveDate;
      draft.effectiveDate.range.ceiling = action.product.effectiveDateCeiling;
      draft.currentProductId = action.product.id;
      return;
    case QTC.LOAD_QUOTE_SUCCESS:
      draft.quote = action.quote;
      draft.loading.state = QTC.LOAD_QUOTE_SUCCESS;
      return;
    case QTC.SEND_RATING_IMPACT:
      if (action.isRatingImpacted) {
        draft.saving.isRatingImpacted = action.isRatingImpacted;
      }
      return;
    case QTC.SEND_SAVE:
      draft.saving.sending = true;
      draft.saving.error = null;
      if (action.step) {
        draft.loading.step = action.step;
      }
      if (action.bind) {
        draft.saving.binding = true;
      }
      if (action.lossHistory) {
        draft.lossHistoryReport.fetch = QTC.FETCH.STATUS.BUSY;
      }
      return;
    case QTC.SAVE_SUCCESS:
      if (action.data) {
        draft.quote = action.data;
      }
      if (action.inputs) {
        draft.quote.inputs = { ...draft.quote.inputs, ...action.inputs };
      }
      if (action.rateResults) {
        // If we have updated rateResults (from step 2: Select) for the selected Carrier (index 0)
        if (draft.quote.carriers[0]) {
          draft.quote.carriers[0].rateResults = action.rateResults;
        }
      }
      draft.saving.sending = false;
      draft.saving.isRatingImpacted = false;
      draft.saving.lastSaved = Date.now();
      draft.saving.error = null;
      if (action.lossHistory) {
        draft.lossHistoryReport.fetch = QTC.FETCH.STATUS.DONE;
      }
      if (action.bind) {
        draft.saving.binding = false;
        draft.payment.bound = true;
      }
      return;
    case QTC.SAVE_ERROR:
      draft.saving.sending = false;
      draft.saving.error = action.message;
      if (action.lossHistory) {
        draft.lossHistoryReport.fetch = QTC.FETCH.STATUS.ERROR;
      }
      return;
    case QTC.PAYMENT_ERROR:
      draft.saving.sending = false;
      draft.payment.error = action.payload.error;
      draft.payment.title = action.payload.title;
      draft.payment.message = action.payload.message;
      draft.payment.errorCode = action.payload.errorCode;
      draft.modals.paymentError = true;
      draft.payment.bound = false;
      draft.saving.binding = false;
      return;
    case QTC.PAYMENT_SUCCESS:
      draft.modals.paymentSuccess = true;
      return;
    case QTC.SET_MODAL_STATE:
      draft.modals[action.modal] = action.isOpen;
      return;
    case QTC.SET_RCE_MODAL_DATA:
      draft.replacementCost = action.payload;
      return;
    case QTC.PUSH_CHANGE_HISTORY:
      draft.changeHistory.showModal = true;
      draft.changeHistory.changes = [
        ...draft.changeHistory.changes,
        action.change
      ];
      return;
    case QTC.CLEAR_CHANGE_HISTORY:
      draft.changeHistory.showModal = false;
      draft.changeHistory.changes = [];
      return;
    case QTC.SET_EFFECTIVE_DATE:
      draft.effectiveDate.value = action.effectiveDate;
      draft.effectiveDate.error = validateEffectiveDate(
        action.effectiveDate,
        add(new Date(), { years: 1 })
      );
      return;
    case LOSS_HISTORY.RESET:
      draft.lossHistoryReport = {
        fetch: QTC.FETCH.STATUS.INITIAL
      };
      return;
    case QTC.UPDATE_ACH_FORM:
      draft.achFormData[action.input?.id] = action.input?.value;
      return;
    case QTC.UPDATE_CC_FORM:
      draft.ccFormData[action.input?.id] = action.input?.value;
      return;
    case QTC.UPDATE_CHECK_FORM:
      draft.checkFormData[action.input?.id] = action.input?.value;
      return;
    case QTC.TOGGLE_SEARCH_MORTGAGEE_MODAL:
      draft.searchMortgagee.initialData = action.payload.initialData;
      draft.searchMortgagee.modalOpen = action.payload.open;
      return;
    case QTC.SAVE_QUEUE.STATUS.IDLE:
      draft.saveQueue.status = QTC.SAVE_QUEUE.STATUS.IDLE;
      return;
    case QTC.SAVE_QUEUE.STATUS.PENDING:
      draft.saveQueue.status = QTC.SAVE_QUEUE.STATUS.PENDING;
      return;
    case QTC.SAVE_QUEUE.STATUS.SUCCESS:
      draft.saveQueue.status = QTC.SAVE_QUEUE.STATUS.SUCCESS;
      return;
    case QTC.SAVE_QUEUE.STATUS.REJECTED:
      draft.saveQueue.status = QTC.SAVE_QUEUE.STATUS.REJECTED;
      return;
    case QTC.SAVE_QUEUE.ATTEMPTED:
      draft.saveQueue.queue.saveAttempted = true;
      draft.saveQueue.queue.props = action.props;
      return;
    case QTC.SAVE_QUEUE.RESET:
      draft.saveQueue = initState.saveQueue;
      return;
    case QTC.UPDATE_QUOTE_UTILITY_ACTIVE_TAB:
      draft.quoteUtilityActiveTab = action.tab;
      return;
  }
}, initState);
